/**
 * CampaignCard - Display card for platform campaigns
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  MoreVertical,
  Play,
  Pause,
  Pencil,
  Trash2,
  Users,
  CheckCircle,
  TrendingDown,
  Mail,
  MessageSquare,
  Clock,
  Eye,
} from 'lucide-react';
import {
  Card,
  CardContent,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
} from '@respectly/ui';
import type { PlatformCampaign, CampaignStep } from '@respectly/shared-types';
import { CampaignStatusBadge } from './CampaignStatusBadge';
import { CampaignTriggerBadge } from './CampaignTriggerBadge';

// ============================================
// Types
// ============================================

interface CampaignCardProps {
  campaign: PlatformCampaign;
  onEdit?: (campaign: PlatformCampaign) => void;
  onView?: (campaign: PlatformCampaign) => void;
  onDelete?: (campaign: PlatformCampaign) => void;
  onActivate?: (campaign: PlatformCampaign) => void;
  onPause?: (campaign: PlatformCampaign) => void;
  className?: string;
}

// ============================================
// Status border color map
// ============================================

const statusBorderColor: Record<string, string> = {
  active: 'border-l-emerald-500',
  paused: 'border-l-amber-400',
  draft: 'border-l-slate-300 dark:border-l-slate-600',
  completed: 'border-l-blue-500',
  archived: 'border-l-red-400',
};

const stepTypeStyles: Record<CampaignStep['type'], { icon: typeof Mail; bg: string; text: string }> = {
  email: { icon: Mail, bg: 'bg-indigo-100 dark:bg-indigo-900/40', text: 'text-indigo-600 dark:text-indigo-400' },
  sms: { icon: MessageSquare, bg: 'bg-purple-100 dark:bg-purple-900/40', text: 'text-purple-600 dark:text-purple-400' },
  wait: { icon: Clock, bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-600 dark:text-amber-400' },
};

// ============================================
// Component
// ============================================

export function CampaignCard({
  campaign,
  onEdit,
  onView,
  onDelete,
  onActivate,
  onPause,
  className,
}: CampaignCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const completionRate =
    campaign.enrolledCount > 0
      ? Math.round((campaign.completedCount / campaign.enrolledCount) * 100)
      : 0;

  const exitRate =
    campaign.enrolledCount > 0
      ? Math.round((campaign.exitedCount / campaign.enrolledCount) * 100)
      : 0;

  const inProgress = campaign.enrolledCount - campaign.completedCount - campaign.exitedCount;

  const borderClass = statusBorderColor[campaign.status] ?? 'border-l-slate-300';

  return (
    <motion.div
      whileHover={{ y: -2 }}
      transition={{ duration: 0.15 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Card
        className={cn(
          'relative overflow-hidden border-l-4 transition-all duration-200',
          borderClass,
          isHovered && 'shadow-md',
          className
        )}
      >
        <CardContent className="p-4">
          {/* Header: name + badges + menu */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1 min-w-0">
              <h3
                className="font-semibold text-slate-900 dark:text-white text-sm leading-snug line-clamp-1 cursor-pointer hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
                onClick={() => onView?.(campaign)}
              >
                {campaign.name}
              </h3>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 flex-shrink-0 -mr-1">
                  <MoreVertical className="h-3.5 w-3.5" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                {onView && (
                  <DropdownMenuItem onClick={() => onView(campaign)}>
                    <Eye className="mr-2 h-4 w-4" />
                    View Details
                  </DropdownMenuItem>
                )}
                {onEdit && (
                  <DropdownMenuItem onClick={() => onEdit(campaign)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                )}
                {onActivate && (campaign.status === 'draft' || campaign.status === 'paused') && (
                  <DropdownMenuItem onClick={() => onActivate(campaign)}>
                    <Play className="mr-2 h-4 w-4" />
                    Activate
                  </DropdownMenuItem>
                )}
                {onPause && campaign.status === 'active' && (
                  <DropdownMenuItem onClick={() => onPause(campaign)}>
                    <Pause className="mr-2 h-4 w-4" />
                    Pause
                  </DropdownMenuItem>
                )}
                {onDelete && campaign.status === 'draft' && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onDelete(campaign)}
                      className="text-red-600 dark:text-red-400"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Badges */}
          <div className="flex flex-wrap items-center gap-1.5 mb-2.5">
            <CampaignStatusBadge status={campaign.status} size="sm" />
            <CampaignTriggerBadge trigger={campaign.trigger} size="sm" />
          </div>

          {/* Description */}
          {campaign.description && (
            <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mb-3 leading-relaxed">
              {campaign.description}
            </p>
          )}

          {/* Step flow pills */}
          {campaign.steps.length > 0 && (
            <div className="flex items-center gap-1 mb-3 flex-wrap">
              {campaign.steps.slice(0, 7).map((step, i) => {
                const cfg = stepTypeStyles[step.type];
                const Icon = cfg.icon;
                return (
                  <div
                    key={step.id || i}
                    title={step.type === 'wait' ? `Wait ${step.delay ?? 0}d` : step.type}
                    className={cn('h-5 w-5 rounded flex items-center justify-center', cfg.bg)}
                  >
                    <Icon className={cn('h-3 w-3', cfg.text)} />
                  </div>
                );
              })}
              {campaign.steps.length > 7 && (
                <span className="text-xs text-slate-400">+{campaign.steps.length - 7}</span>
              )}
            </div>
          )}

          {/* Stats row */}
          <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-800">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <Users className="h-3 w-3" />
                <span className="tabular-nums font-medium text-slate-700 dark:text-slate-300">{campaign.enrolledCount.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <CheckCircle className="h-3 w-3 text-emerald-500" />
                <span className="tabular-nums font-medium text-slate-700 dark:text-slate-300">{campaign.completedCount.toLocaleString()}</span>
              </div>
              {exitRate > 0 && (
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <TrendingDown className="h-3 w-3 text-red-400" />
                  <span className="tabular-nums font-medium text-red-500">{exitRate}%</span>
                </div>
              )}
            </div>
            <span className="text-xs font-medium text-slate-500 tabular-nums">
              {completionRate}%
            </span>
          </div>

          {/* Progress bar */}
          <div className="mt-2 h-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full flex">
              <div
                className="h-full bg-emerald-500 rounded-l-full transition-all"
                style={{ width: `${completionRate}%` }}
              />
              {inProgress > 0 && campaign.enrolledCount > 0 && (
                <div
                  className="h-full bg-blue-400 transition-all"
                  style={{ width: `${(inProgress / campaign.enrolledCount) * 100}%` }}
                />
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ============================================
// Skeleton Loader
// ============================================

export function CampaignCardSkeleton() {
  return (
    <Card className="relative overflow-hidden border-l-4 border-l-slate-200 dark:border-l-slate-700">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="h-4 w-3/4 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
          <div className="h-7 w-7 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
        </div>
        <div className="flex gap-1.5 mb-2.5">
          <div className="h-5 w-16 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse" />
          <div className="h-5 w-20 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse" />
        </div>
        <div className="h-3 w-full bg-slate-200 dark:bg-slate-700 rounded animate-pulse mb-1" />
        <div className="h-3 w-2/3 bg-slate-200 dark:bg-slate-700 rounded animate-pulse mb-3" />
        <div className="flex gap-1 mb-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-5 w-5 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
          ))}
        </div>
        <div className="pt-3 border-t border-slate-100 dark:border-slate-800">
          <div className="h-1 w-full bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
        </div>
      </CardContent>
    </Card>
  );
}
